import React from "react";
import { CONDITIONALS, ALARM_TYPES } from "constants/globals";
import { setAlarmsCompleted } from "ducks/instrumentDetails/actions";
import type { ComponentType } from "react";
import { connect } from "react-redux";
import type { ReduxState } from "ducks/types";
import history from "utils/browserHistory";
import { bindActionCreators } from "redux";
import type { Dispatch } from "redux";
import SnackbarAlarm from "components/Common/Modals/SnackbarAlarm";

import type { Props, State } from "./types";
import withHook from "./withHook";
import { createTtlDedupe, NOTIFICATION_DEDUPE_MS } from "utils/ttlDedupe";

const W3CWebSocket = require("websocket").w3cwebsocket;

const UrlSocket = process.env.REACT_APP_SOCKET_URL;
let timeOutId: ?TimeoutID = null;
const WS_CONNECTING = 0;
const WS_OPEN = 1;

class NotificationSocket extends React.Component<Props, State> {
  state = {
    alarmMessage: "",
    alarmId: "",
    instrumentId: "",
    client: null
  };

  shouldPresentAlarm = createTtlDedupe(NOTIFICATION_DEDUPE_MS);

  connecting: boolean = false;

  timeOutDisconnect = () => {
    timeOutId = setTimeout(() => {
      if (this.state.client) {
        this.state.client.close();
      }
      this.connecting = false;
      this.connectSocket();
    }, 31000);
  };

  // eslint-disable-next-line class-methods-use-this
  connectSocket() {
    const { user } = this.props;
    const existing = this.state.client;

    // Avoid needless reconnect on prop/completion churn while socket is live
    if (
      this.connecting ||
      (existing &&
        (existing.readyState === WS_CONNECTING ||
          existing.readyState === WS_OPEN))
    ) {
      return;
    }

    if (user) {
      this.connecting = true;
      const client = new W3CWebSocket(
        `${UrlSocket || ""}?token=${user.accessToken.toString()}`,
        "echo-protocol"
      );
      this.setState({ client });

      // eslint-disable-next-line no-underscore-dangle
      const _this = this;

      // eslint-disable-next-line func-names
      client.onerror = function() {
        _this.connecting = false;
        _this.setState({ client: null }, () => {
          _this.connectSocket();
        });
      };

      // eslint-disable-next-line func-names
      client.onopen = function() {
        _this.connecting = false;
        // $FlowFixMe
        client.send(
          JSON.stringify({
            request: "identify",
            token: user ? user.accessToken.toString() : ""
          })
        );
      };

      // eslint-disable-next-line func-names
      client.onmessage = function(e) {
        if (typeof e.data !== "string") {
          return;
        }

        const socketData = JSON.parse(e.data);
        if (socketData.type === "Login") {
          history.push("/logout");
          return;
        }

        if (socketData.type === "Error") {
          _this.connecting = false;
          _this.setState({ client: null }, () => {
            _this.connectSocket();
          });
          return;
        }

        if (socketData.type === "Message") {
          return;
        }

        if (socketData.type === "Ping") {
          clearTimeout(timeOutId);
          _this.timeOutDisconnect();

          client.send(
            JSON.stringify({
              request: "pong",
              token: user ? user.accessToken.toString() : ""
            })
          );
          return;
        }

        if (socketData.type === "Alarm") {
          const alarmId = socketData.alarm.alarmId;
          if (!_this.shouldPresentAlarm(alarmId)) {
            return;
          }

          const intrumentSplit = socketData.alarm.uniqueInstrumentKey.split(
            "/"
          );
          _this.setState({
            alarmMessage: `${intrumentSplit[intrumentSplit.length - 1]} - ${
              socketData.alarm.name
            } (${_this.getDescription(socketData.alarm)})`,
            instrumentId: socketData.alarm.uniqueInstrumentKey,
            alarmId: `alarm:${alarmId}`
          });

          _this.setAlarmsCompleted(socketData.alarm.uniqueInstrumentKey);
          _this.props.updateAlarm(
            socketData.alarm.alarmId,
            socketData.alarm.status
          );
        }
      };
    }
  }

  componentDidUpdate() {
    if (this.props.user && !this.state.client && !this.connecting) {
      this.connectSocket();
    }
  }

  // eslint-disable-next-line class-methods-use-this
  componentWillUnmount() {
    clearTimeout(timeOutId);
  }

  setAlarmsCompleted = (instrument: string) => {
    const newAlarmsCompleted = this.props.alarmsCompleted.concat(instrument);
    this.props.setAlarmsCompleted(newAlarmsCompleted);
    setTimeout(() => {
      const cleanAlarms = this.props.alarmsCompleted.filter(
        item => item !== instrument
      );
      this.props.setAlarmsCompleted(cleanAlarms || []);
    }, 20000);
  };

  getDescription = (alarm: Object) => {
    let description = "";
    const condition =
      Object.keys(CONDITIONALS).find(
        key => CONDITIONALS[key].value === alarm.condition
      ) || "LESS";
    if (condition === CONDITIONALS.BETWEEN.value.toUpperCase()) {
      description = `${CONDITIONALS[condition].text} $${
        Number.isInteger(alarm.value)
          ? alarm.value
          : Number(alarm.value).toFixed(2)
      }% y $${
        Number.isInteger(alarm.rangeValue)
          ? alarm.rangeValue
          : Number(alarm.rangeValue).toFixed(2)
      }%`;
    } else {
      description = `${CONDITIONALS[condition].text} $${
        Number.isInteger(alarm.value)
          ? alarm.value
          : Number(alarm.value).toFixed(2)
      }%`;
    }

    return alarm.alarmType === ALARM_TYPES.PERCENTAGE
      ? description.replace(/[$]/g, "")
      : description.replace(/%/g, "");
  };

  async componentDidMount() {
    this.connectSocket();
    this.timeOutDisconnect();
  }

  render() {
    const { alarmMessage, instrumentId, alarmId } = this.state;

    return (
      <SnackbarAlarm
        message={alarmMessage}
        notificationId={alarmId || undefined}
        instrumentId={instrumentId}
        handleClose={(event, reason) => {
          if (reason === "clickaway") {
            return;
          }
          this.setState({ alarmMessage: "" });
        }}
        handleClick={() => {}}
        orderId={null}
        widthMessage={150}
      />
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  alarmsCompleted: state.instrumentDetails.alarmsCompleted
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return bindActionCreators(
    {
      setAlarmsCompleted
    },
    dispatch
  );
};

const ConnectedComponent: ComponentType<*> = connect(
  mapStateToProps,
  mapDispatchToProps
)(NotificationSocket);

export default withHook(ConnectedComponent);
